import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  X,
  Plus,
  Eye,
  EyeOff,
  Info,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import EnvironmentVariable from '../../../../types/BotBuilder/EnvironmentVariable';
import {
  validateEnvironmentVariableKey,
  validateEnvironmentVariableValue
} from '../../utils/environmentVariableValidation';

interface FormValues {
  variables: EnvironmentVariable[];
}

interface EnvironmentVariablesProps {
  variables: EnvironmentVariable[];
  onChange: (variables: EnvironmentVariable[]) => void;
}

function EnvironmentVariables({
  variables,
  onChange
}: Readonly<EnvironmentVariablesProps>): React.ReactElement {
  const isInternalChange = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { register, control, getValues, watch, reset } = useForm<FormValues>({
    defaultValues: { variables },
    mode: 'onChange'
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variables'
  });

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    reset({ variables });
  }, [variables, reset]);

  const notifyChange = useCallback(() => {
    const currentValues = getValues('variables');
    const cleanedVariables = currentValues.map(({ key, value, isSecret }) => ({
      key,
      value,
      isSecret
    }));
    if (JSON.stringify(cleanedVariables) !== JSON.stringify(variables)) {
      isInternalChange.current = true;
      onChange(cleanedVariables);
    }
  }, [getValues, onChange, variables]);

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
      setTimeout(notifyChange, 0);
    },
    [remove, notifyChange]
  );

  const handleAppend = useCallback(() => {
    append({ key: '', value: '', isSecret: false });
    setTimeout(notifyChange, 0);
  }, [append, notifyChange]);

  const toggleSecret = useCallback(
    (index: number) => {
      const current = getValues(`variables.${index}`);
      update(index, { ...current, isSecret: !current.isSecret });
      setTimeout(notifyChange, 0);
    },
    [getValues, update, notifyChange]
  );

  const watchedVariables = watch('variables');

  return (
    <div className='flex flex-col gap-2 overflow-hidden'>
      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => setIsExpanded(!isExpanded)}
          className='text-gray-500 hover:text-gray-700 p-0.5 -ml-1'
        >
          {isExpanded ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
        </button>
        <Label className='text-sm font-medium'>Environment variables</Label>
        <CustomTooltip
          content={
            <p>
              Secrets like API keys. Access via{' '}
              <code className='text-green-300'>
                os.environ[&apos;KEY&apos;]
              </code>
            </p>
          }
          sideOffset={5}
        >
          <button type='button' className='inline-flex'>
            <Info className='w-4 h-4 text-gray-400 cursor-help' />
          </button>
        </CustomTooltip>
      </div>
      {isExpanded &&
        fields.map((field, index) => {
          const keyReg = register(`variables.${index}.key`);
          const valueReg = register(`variables.${index}.value`);
          const currentVar = watchedVariables?.[index];
          const keyError = currentVar
            ? validateEnvironmentVariableKey(currentVar.key)
            : null;
          const valueError = currentVar
            ? validateEnvironmentVariableValue(currentVar.value)
            : null;

          return (
            <div
              key={field.id}
              className='flex flex-col gap-2 p-3 border rounded-lg bg-white relative'
            >
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleRemove(index)}
                className='h-6 w-6 absolute top-2 right-2 text-muted-foreground hover:text-destructive'
              >
                <X className='h-4 w-4' />
              </Button>
              <div className='flex flex-col gap-1 pr-6'>
                <CustomInput
                  label='Key'
                  placeholder='API_KEY'
                  containerClassName='mb-0'
                  className={`bg-gray-50 ${keyError ? 'border-red-500' : ''}`}
                  {...keyReg}
                  onChange={(e) => {
                    keyReg.onChange(e);
                    setTimeout(notifyChange, 0);
                  }}
                />
                {keyError && (
                  <span className='text-xs text-red-500'>{keyError}</span>
                )}
              </div>
              <div className='flex gap-2 items-end'>
                <div className='flex flex-col gap-1 flex-1'>
                  <CustomInput
                    label='Value'
                    placeholder='Enter value'
                    type={currentVar?.isSecret ? 'password' : 'text'}
                    containerClassName='mb-0'
                    className={`bg-gray-50 ${valueError ? 'border-red-500' : ''}`}
                    {...valueReg}
                    onChange={(e) => {
                      valueReg.onChange(e);
                      setTimeout(notifyChange, 0);
                    }}
                  />
                  {valueError && (
                    <span className='text-xs text-red-500'>{valueError}</span>
                  )}
                </div>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => toggleSecret(index)}
                  className='h-9 w-9 shrink-0'
                  title={currentVar?.isSecret ? 'Show value' : 'Hide value'}
                >
                  {currentVar?.isSecret ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      {isExpanded && (
        <Button
          variant='outline'
          size='sm'
          onClick={handleAppend}
          className='w-fit'
        >
          <Plus className='h-4 w-4 mr-1' />
          Add variable
        </Button>
      )}
    </div>
  );
}

export default EnvironmentVariables;
